import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { PermitStatusBadge } from "@app.automotus.io/components/permits/PermitStatusBadge";
import { PermitStatus, PermitApplicationStatus } from "common/types/permits";
import { PermitStatusAlert, PermitDetailsSection, VehiclesSection, DocumentsSection } from "./components";
import type { Vehicle } from "./components/VehiclesSection";
import type { Document } from "./components/DocumentsSection";

export interface PermitDetailContentProps {
  permitId: string;
  displayId: string;
  permitName: string;
  permitStatus: PermitStatus;
  applicationStatus: PermitApplicationStatus;
  permitType: string;
  submittedDate?: string | null;
  approvedDate?: string | null;
  expiryDate?: string | null;
  issuingAuthority: string;
  reviewComment?: string | null;
  vehicles: Vehicle[];
  documents: Document[];
  vehicleCount: number;
  onBack: () => void;
  onStartNewApplication: () => void;
  onViewDocument: (documentId: string) => void;
}

export const PermitDetailContent: React.FC<PermitDetailContentProps> = ({
  displayId,
  permitName,
  permitStatus,
  applicationStatus,
  permitType,
  submittedDate,
  approvedDate,
  expiryDate,
  issuingAuthority,
  reviewComment,
  vehicles,
  documents,
  vehicleCount,
  onBack,
  onStartNewApplication,
  onViewDocument,
}) => {
  return (
    <Box>
      {/* Back button */}
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: { xs: 2, md: 3 }, color: "text.secondary" }}
      >
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
          Back to Permits
        </Box>
        <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
          Back
        </Box>
      </Button>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "flex-start" },
            gap: 2,
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: { xs: 1, sm: 2 },
                mb: 1,
              }}
            >
              <Typography variant="h4" component="h1" fontWeight={600}>
                {permitName}
              </Typography>
              <PermitStatusBadge type="permit" status={permitStatus} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Permit ID: {displayId}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main content */}
        <Grid item xs={12} lg={8}>
          <PermitStatusAlert
            permitStatus={permitStatus}
            applicationStatus={applicationStatus}
            reviewComment={reviewComment}
            expiryDate={expiryDate}
            issuingAuthority={issuingAuthority}
            onStartNewApplication={onStartNewApplication}
          />

          <PermitDetailsSection
            permitType={permitType}
            submittedDate={submittedDate}
            approvedDate={approvedDate}
            expiryDate={expiryDate}
            issuingAuthority={issuingAuthority}
          />

          <VehiclesSection vehicles={vehicles} />

          <DocumentsSection documents={documents} onViewDocument={onViewDocument} />
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Quick Info
            </Typography>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
              <LocationOnIcon color="action" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Issuing Authority
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {issuingAuthority}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
              <DirectionsCarIcon color="action" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Vehicles
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {vehicleCount} {vehicleCount === 1 ? "vehicle" : "vehicles"}
                </Typography>
              </Box>
            </Box>

            {expiryDate && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <CalendarTodayIcon color="action" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Valid Until
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(expiryDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PermitDetailContent;
